import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useToast } from '@/hooks/use-toast';
import { updateQuiz, deleteQuiz } from '@/services/api/chatbot';
import type { ChatbotQuiz } from '@/types/chatbot';
import { Trash2 } from 'lucide-react';

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    quiz: ChatbotQuiz | null;
    chatId: number;
    onDeleted?: () => void;
}

export default function QuizEditModal({ open, onOpenChange, quiz, chatId, onDeleted }: Props) {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [name, setName] = useState('');
    const [weight, setWeight] = useState(1);
    const [isActive, setIsActive] = useState(true);
    const [deleteConfirm, setDeleteConfirm] = useState(false);

    useEffect(() => {
        if (open && quiz) {
            setName(quiz.name);
            setWeight(quiz.weight || 1);
            setIsActive(!!quiz.is_active);
        }
    }, [open, quiz]);

    const saveMut = useMutation({
        mutationFn: () => updateQuiz(quiz!.id, {
            name,
            weight: Math.max(1, Number(weight) || 1),
            is_active: isActive,
        }),
        onSuccess: () => {
            toast({ title: 'Quiz atualizado!' });
            queryClient.invalidateQueries({ queryKey: ['chatbot-quizzes', chatId] });
            onOpenChange(false);
        },
        onError: (e: any) => toast({
            variant: 'destructive', title: 'Erro ao salvar',
            description: e.response?.data?.detail || e.message,
        }),
    });

    const deleteMut = useMutation({
        mutationFn: () => deleteQuiz(quiz!.id),
        onSuccess: () => {
            toast({ title: 'Quiz deletado!' });
            queryClient.invalidateQueries({ queryKey: ['chatbot-quizzes', chatId] });
            setDeleteConfirm(false);
            onOpenChange(false);
            onDeleted?.();
        },
        onError: (e: any) => toast({
            variant: 'destructive', title: 'Erro ao deletar',
            description: e.response?.data?.detail || e.message,
        }),
    });

    if (!quiz) return null;

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Configurar quiz</DialogTitle>
                        <DialogDescription>
                            Quizzes do mesmo chat sao sorteados aleatoriamente pelo peso.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-2">
                        <div>
                            <Label>Nome</Label>
                            <Input value={name} onChange={(e) => setName(e.target.value)}
                                placeholder="Nome do quiz" />
                        </div>

                        <div>
                            <Label>Peso na randomizacao</Label>
                            <Input type="number" min={1} value={weight}
                                onChange={(e) => setWeight(Number(e.target.value) || 1)} />
                            <p className="text-[10px] text-slate-500 mt-1">
                                Quizzes com peso maior aparecem mais. Ex: peso=2 dobra a chance vs peso=1.
                            </p>
                        </div>

                        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-md">
                            <div>
                                <div className="text-sm font-medium">Ativo</div>
                                <div className="text-[10px] text-slate-500">
                                    Quizzes inativos nao entram no sorteio.
                                </div>
                            </div>
                            <Switch checked={isActive} onCheckedChange={setIsActive} />
                        </div>
                    </div>

                    <DialogFooter className="flex !justify-between">
                        <Button variant="outline"
                            onClick={() => setDeleteConfirm(true)}
                            className="text-red-600 border-red-200 hover:bg-red-50">
                            <Trash2 className="w-4 h-4 mr-1" />Deletar quiz
                        </Button>
                        <div className="flex gap-2">
                            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
                            <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
                                Salvar
                            </Button>
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <ConfirmDialog
                open={deleteConfirm}
                onOpenChange={setDeleteConfirm}
                title="Deletar quiz?"
                description={`"${quiz.name}" sera removido junto com TODAS as perguntas e respostas dentro dele. Acao irreversivel.`}
                confirmLabel="Deletar"
                variant="danger"
                isLoading={deleteMut.isPending}
                onConfirm={() => deleteMut.mutate()}
            />
        </>
    );
}
